/**
 * The portfolio definition input (LAR-1).
 *
 * Presentational, like its DSL counterpart: the editor holds the text, runs the debounced
 * validation and renders the result. This component's only job beyond the input itself is to say
 * when a check is in flight, because portfolio validation queries the database for every sleeve and
 * is therefore slow enough to look like nothing happening.
 *
 * The input is `DefinitionInput`, shared with the DSL form since the textarea stopped being
 * always-on — a portfolio document is JSON and a strategy is CLJ, and a textarea handed thirty
 * million characters does not care which.
 */
import { DefinitionInput, type StoredDefinition } from './DefinitionInput';

interface Props {
  value: string;
  /** The definition is on the server and not in `value` — see `DefinitionInput` (#418). */
  stored?: StoredDefinition | null;
  onLoadStored?: () => Promise<unknown>;
  disabled?: boolean;
  /** A debounced validation is in flight. */
  validating?: boolean;
  onChange: (json: string) => void;
}

export function PortfolioStrategyForm({ value, stored, onLoadStored, disabled, validating, onChange }: Props) {
  return (
    <DefinitionInput
      id="strategy-portfolio"
      label="Portfolio JSON"
      accept=".json"
      uploadLabel="Upload portfolio JSON"
      value={value}
      stored={stored}
      onLoadStored={onLoadStored}
      disabled={disabled}
      placeholder={'{\n  "schema": "vrk-portfolio/v1",\n  "name": "...",\n  "sleeves": [ ... ]\n}'}
      busyNote={validating ? 'Checking sleeves…' : undefined}
      onChange={onChange}
      help={
        <>
          A <code className="font-mono">vrk-portfolio/v1</code> document. Every sleeve it names must
          already exist as one of your strategies.
        </>
      }
    />
  );
}
